import { quoteApi, quoteSupplierItemApi, supplierApi } from "@/main/api";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KOGAutoCompletePaginationControlled from "@/main/components/shared/inputAutoCompleteWithPagination/autoCompletePaginationControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KOGModalConfirm from "@/main/components/shared/KogmodalConfirm/modalConfirm";
import ModalImages from "@/main/components/shared/modalImages/modalImages";
import * as StylesShared from "@/main/components/shared/styles";
import { KeyValue } from "@/main/models/common";
import { SearchTermRequest } from "@/main/models/common/searchTermRequest";
import {
	BuyerPartAnswerRequest,
	DirectAssignRequest,
	ReasonFilterRequest,
	SupplierFavoriteRequest,
} from "@/main/models/request";
import { QuoteDirectAssignItemsResponse } from "@/main/models/response";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import {
	getDefaultCountryOptionDrop,
	getTranslateByKey,
	isNullUndefined,
	isNullUndefinedEmpty,
} from "@/main/services/utils";
import { currencyToNumber } from "@/main/services/utilsNumber";
import { userIsSupplier } from "@/main/services/utilsUser";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getPartTypesByDirectAssignCriteria, usePartTypeReducer } from "@/main/store/partType";
import { getReason, useReasonReducer } from "@/main/store/reason";
import { isEmpty, isNil } from "lodash";
import React, { useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTheme } from "styled-components";
import BoxGrid from "../../../boxGrid/boxGrid";
import LoadingComponent from "../../../loading/loadingComponent";
import ResultNotFoundComponent from "../../../resultNotFound/resultNotFoundComponent";
import TooltipComponent from "../../../tooltip/tooltipComponent";
import ModalConfirmBuyerManualAnswer from "../modalConfirmBuyerManualAnswer/modalConfirmBuyerManualAnswer";
import {
	AutoFillForms,
	BuyerPartForms,
	INITIAL_FORM_STATE,
	INITIAL_FORM_STATE_AUTOFILL,
	PartBuyerManualAnswerForm,
} from "./modalManualAnswerUtils";
import PartBuyerManualAnswer from "./partBuyerManualAnswer/partBuyerManualAnswer";
import * as S from "./styles";
import { AUTOFILL_IDS, GRID_COL, useGridTabNavigation } from "./useGridTabNavigation";

import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import { PartTypeSupplierIgnoreRequest } from "@/main/models/request/partTypeSupplierIgnoreRequest";
import { AutomaticActionEnum } from "@/main/services/enum/AutomaticActionEnum";
import { saveAutomaticAction } from "@/main/store/automaticAction";
import Info from "@mui/icons-material/Info";

interface IModalBuyerManualAnswerProps {
	isOpenModal: boolean;
	guidQuote: string;
	guidClaim?: string;
	guidCompanyBuyer: string;
	canDownloadImages: boolean;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
	onSuccess: () => void;
	setBlockedCloseSidebar: React.Dispatch<React.SetStateAction<boolean>>;
}

function ModalBuyerManualAnswer({
	closeModal,
	guidCompanyBuyer,
	isOpenModal,
	guidQuote,
	onSuccess,
	setBlockedCloseSidebar,
}: IModalBuyerManualAnswerProps) {
	const methods = useForm<BuyerPartForms>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	const methodsAutoFill = useForm<AutoFillForms>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE_AUTOFILL,
	});

	const { reset: resetAutoFill } = methodsAutoFill;

	const theme = useTheme();

	const { trigger, getValues, setValue, watch, reset, control } = methods;

	const { fields, replace } = useFieldArray<BuyerPartForms>({
		control,
		name: "parts",
	});

	const { selectedCol, toggleSelectedCol, clearSelectedCol, handleGridTab } = useGridTabNavigation(fields.length);

	useEffect(() => {
		const subscription = watch((value) => {
			const hasAny = value.parts?.some((p) => p?.price || p?.supplier || p?.partType || p?.deadline) ?? false;
			setBlockedCloseSidebar(hasAny);
		});
		return () => subscription.unsubscribe();
	}, [watch, setBlockedCloseSidebar]);

	const dispatch = useDispatch();

	const [isLoadingSave, setIsLoadingSave] = useState(false);
	const [openModalConfirmBuyerManualAnswer, setOpenModalConfirmBuyerManualAnswer] = useState<boolean>(false);

	const [isLoadingGet, setIsLoadingGet] = useState(false);
	const [dataParts, setDataParts] = useState<QuoteDirectAssignItemsResponse[]>([]);
	const [openModalConfirmGeneratePurchaseOrder, setOpenModalConfirmGeneratePurchaseOrder] = useState<boolean>(false);

	const { reason: reasonReducer } = useReasonReducer();
	const { countriesUser: countryReducer } = useCountryReducer();
	const { partType: partTypeReducer } = usePartTypeReducer();

	const watcherReasonType = useWatch({ control, name: "reason" });
	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

	function getFilledParts() {
		let parts = getValues("parts");
		parts = parts.filter((part) => !isNullUndefinedEmpty(part.price));
		return parts;
	}

	async function validateForm() {
		const isValid = await trigger();
		if (!isValid) {
			toast.error(getTranslateByKey("information.requiredFieldsMissing", true));
			return false;
		}
		const parts = getFilledParts();
		if (parts == null || parts.length == 0) {
			toast.error(getTranslateByKey("information.atLeastOneAnswer", true));
			return false;
		}
		return true;
	}

	async function onSubmit() {
		const isValid = await validateForm();
		if (!isValid) return;

		const partsAnswered = getFilledParts();
		const ignoreRequest: PartTypeSupplierIgnoreRequest = {
			partTypeSupplierIgnores: partsAnswered.map((part) => ({
				guidQuoteItem: part.guidQuoteItem,

				guidSupplier: String(part.supplier?.key),

				guidPartType: part.partType?.key && part.partType.key !== "" ? String(part.partType.key) : "",

				idPartType: part.partType?.default !== undefined ? Number(part.partType.default) : undefined,

				guidQuoteSupplierItem: "",
			})),
		};

		const response = await supplierApi.getPartTypeSupplierIgnore(ignoreRequest);
		const resultData = response?.data?.result ?? [];

		if (!resultData || resultData.length === 0) {
			setOpenModalConfirmBuyerManualAnswer(true);
			return;
		}

		const messageHtml = buildSupplierIgnoreMessage(resultData);

		const isConfirmModal = await getConfirmation(messageHtml);
		if (!isConfirmModal) return;

		setOpenModalConfirmBuyerManualAnswer(true);
	}
	function buildSupplierIgnoreMessage(resultData: any[]) {
		return (
			<S.ContainnerModalConfirmation>
				<span className="title">{getTranslateByKey("information.suppliersDoNotSupportPartTypes")}</span>

				<div className="list">
					{resultData.map((i, index) => (
						<p key={index}>{i.supplierInfo}</p>
					))}
				</div>

				<span className="question">{getTranslateByKey("information.continueAnyway")}</span>
			</S.ContainnerModalConfirmation>
		);
	}

	async function Save(): Promise<boolean> {
		const isReasonValid = await trigger("reason");
		if (!isReasonValid || isNullUndefinedEmpty(watcherReasonType?.key)) {
			toast.error(getTranslateByKey("information.reasonRequired", true));
			return false;
		}
		const reasonRequiresComplement = watcherReasonType?.default === "1";
		if (reasonRequiresComplement) {
			const isReasonComplementValid = await trigger("reasonComplement");
			if (!isReasonComplementValid || isNullUndefinedEmpty(getValues("reasonComplement"))) {
				toast.error(getTranslateByKey("information.reasonRequired", true));
				return false;
			}
		}

		const partsAnswered = getFilledParts();
		const directAssginRequest: DirectAssignRequest = {
			guidReason: String(watcherReasonType?.key),
			reasonComplement: String(getValues("reasonComplement")),
			buyerPartAnswers: partsAnswered.map((part) => {
				const answerReq: BuyerPartAnswerRequest = {
					guidQuoteItem: part.guidQuoteItem,
					guidSupplier: String(part.supplier?.key),
					guidPartType: String(part.partType?.key),
					answeredPrice: currencyToNumber(part.price) || 0,
					deliveryDeadline: Number(part.deadline),
				};
				return answerReq;
			}),
		};

		setIsLoadingSave(true);

		try {
			const res = await quoteSupplierItemApi.saveBuyerPartAsnwer(directAssginRequest);
			const { result } = res.data;

			if (!isNil(result) && result.canGenerateOrder) {
				setOpenModalConfirmGeneratePurchaseOrder(true);
			} else {
				handleSuccessfullySaveBuyerPartAnswer();
			}

			toast.success(getTranslateByKey("information.partAnswerSuccess"));
			return true;
		} finally {
			setIsLoadingSave(false);
		}
	}

	function handleSuccessfullySaveBuyerPartAnswer() {
		// Notation: The parts were already saved, so there is nothing left to lose when closing the sidebar
		setBlockedCloseSidebar(false);
		onSuccess();
		closeModal();
	}

	function loadParts() {
		setIsLoadingGet(true);

		quoteApi
			.getDirectAssignItems(guidQuote)
			.then((response) => {
				const {
					data: { result },
				} = response;

				if (isNullUndefined(result) || result.length === 0) return;

				setDataParts(result);
				const formattedResult = result.map((item: QuoteDirectAssignItemsResponse) => {
					const answerItem: PartBuyerManualAnswerForm = {
						guidQuoteItem: item.guidQuoteItem,
						supplier: null,
						partType: null,
						price: "",
						deadline: null,
					};
					return answerItem;
				});

				replace(formattedResult);
			})
			.finally(() => setIsLoadingGet(false));
	}

	useEffect(() => {
		if (countryReducer.data === null) dispatch(getCountriesUser());
	}, []);

	useEffect(() => {
		const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
		if (countryUser === null) return;

		const requestReason: ReasonFilterRequest = {
			guidCountry: String(countryUser.key),
			idReasonType: ReasonTypeEnum.DirectAssign,
		};
		dispatch(getReason(requestReason));
	}, [JSON.stringify(countryReducer.data)]);

	useEffect(() => {
		if (isOpenModal) {
			loadParts();
			setValue("reason", null);
			setValue("reasonComplement", "");
			reset(INITIAL_FORM_STATE);
			resetAutoFill(INITIAL_FORM_STATE_AUTOFILL);
			dispatch(getPartTypesByDirectAssignCriteria({ guidQuote }));
		}
	}, [isOpenModal]);

	const autofillSupplierWacher = useWatch({
		control: methodsAutoFill.control,
		name: "supplier",
	});

	useEffect(() => {
		setAllValuesParts("supplier", autofillSupplierWacher);
	}, [autofillSupplierWacher]);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			const target = e.target as HTMLElement;
			if (!target.closest("#id-general-boxgrid")) {
				clearSelectedCol();
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	async function getSuppliersFavoriteAPI(requestPagination: SearchTermRequest) {
		const request: SupplierFavoriteRequest = {
			...requestPagination,
			guidCompanyBuyer: guidCompanyBuyer,
		};
		const response = await supplierApi.getFavoriteSuppliersByCompanyBuyer(request);
		return response;
	}

	function setAllValuesParts(key: "supplier" | "partType" | "deadline", value: KeyValue | number | null) {
		for (let index = 0; index < dataParts.length; index++) {
			setValue(`parts.${index}.${key}`, value, {
				shouldValidate: false,
				shouldDirty: true,
				shouldTouch: false,
			});
		}
	}

	if (isLoadingGet || countryReducer.isLoading || reasonReducer.isLoading) return <LoadingComponent />;
	if (isNullUndefined(fields) || fields.length === 0) return <ResultNotFoundComponent />;

	const sizeColumns = ["10%", "11%", "15%", "15%", "16%", "11%"];

	function renderSelectableHeader(
		label: string,
		col: (typeof GRID_COL)[keyof typeof GRID_COL],
		selectedCol: (typeof GRID_COL)[keyof typeof GRID_COL] | null,
		toggleSelectedCol: (col: (typeof GRID_COL)[keyof typeof GRID_COL]) => void,
	) {
		return (
			<StylesShared.RowHeaderTextCenter
				onClick={() => toggleSelectedCol(col)}
				className={selectedCol === col ? "col-selected" : ""}
				style={{ cursor: "pointer", userSelect: "none" }}
			>
				{getTranslateByKey(label)}
			</StylesShared.RowHeaderTextCenter>
		);
	}

	function focusAutofillField(id: string) {
		const el = document.getElementById(id) as HTMLInputElement | null;
		el?.focus();
	}

	function handleAutofillTab(e: React.KeyboardEvent, prevId: string | null, nextId: string | null) {
		if (e.key !== "Tab") return;

		if (e.shiftKey) {
			if (!prevId) return;
			e.preventDefault();
			focusAutofillField(prevId);
		} else {
			if (!nextId) return;
			e.preventDefault();
			focusAutofillField(nextId);
		}
	}

	function handleConfirmGeneratePurchaseOrder() {
		dispatch(
			saveAutomaticAction({
				idAction: AutomaticActionEnum.GenerateOrderBySaveBuyerPartAsnwer,
				id: guidQuote,
			}),
		);

		handleSuccessfullySaveBuyerPartAnswer();
	}

	function handleCloseModalConfirmGeneratePurchaseOrder() {
		setOpenModalConfirmGeneratePurchaseOrder(false);
		handleSuccessfullySaveBuyerPartAnswer();
	}

	return (
		<FormProvider {...methods}>
			<S.Container
				onClick={(e) => {
					const target = e.target as HTMLElement;
					if (!target.closest(".ignore-click") && !target.closest("#id-general-boxgrid")) {
						clearSelectedCol();
					}
				}}
			>
				<S.ContainerAutoFill>
					<S.AutofillLabel>
						<span>{getTranslateByKey("information.autofill")} </span>
						<TooltipComponent title={getTranslateByKey("information.tooltipAutofill")} placement="right">
							<Info style={{ color: theme.pantone2726C }} fontSize="small" />
						</TooltipComponent>
					</S.AutofillLabel>

					<KOGAutoCompletePaginationControlled
						idScreen={`autofill-id-supplier`}
						name={"supplier"}
						label={getTranslateByKey("information.supplier")}
						loadingMessage={`${getTranslateByKey("information.searchingForSuppliers")}...`}
						control={methodsAutoFill.control}
						getData={getSuppliersFavoriteAPI}
						typeInput="outlined"
						className="custom-styles-supplier"
						onKeyDown={(e) => handleAutofillTab(e, null, AUTOFILL_IDS.partType)}
					/>

					<KogAutoCompleteControlled
						id={`autofill-id-buyerManualAnswer-select-partType`}
						nameCustom={"partType"}
						label={getTranslateByKey("information.partTypes")}
						control={methodsAutoFill.control}
						options={partTypeReducer.data}
						isLoading={partTypeReducer.isLoading}
						onChangeCustom={(value) => setAllValuesParts("partType", value)}
						onKeyDown={(e) => handleAutofillTab(e, AUTOFILL_IDS.supplier, AUTOFILL_IDS.deadline)}
						variant="outlined"
						style={{ width: "100%" }}
					/>

					<KogInputTextControlledNew
						id={`autofill-id-buyerManualAnswer-txt-deadline`}
						name={"deadline"}
						variant="outlined"
						label={getTranslateByKey("information.deadline")}
						hasOnlyNumberInteger
						onChangeCustom={(value) =>
							setAllValuesParts("deadline", !isNil(value) && !isEmpty(value) ? Number(value) : null)
						}
						control={methodsAutoFill.control}
						inputProps={{
							maxLength: 3,
							className: "customInputHeight",
						}}
						onKeyDown={(e) => handleAutofillTab(e, AUTOFILL_IDS.partType, null)}
					/>
				</S.ContainerAutoFill>

				<StylesShared.GridWrapper data-selected-col={selectedCol ?? "none"}>
					<BoxGrid
						sizeColumns={sizeColumns}
						rowHeaders={[
							<StylesShared.RowHeaderTextLeft key="0">
								{getTranslateByKey("information.partDescription")}
							</StylesShared.RowHeaderTextLeft>,
							<StylesShared.RowHeaderTextCenter key="1">
								{getTranslateByKey("information.referencePriceAndQuotedPrice")}
							</StylesShared.RowHeaderTextCenter>,
							renderSelectableHeader("information.supplier", GRID_COL.SUPPLIER, selectedCol, toggleSelectedCol),
							renderSelectableHeader("information.partTypes", GRID_COL.PART_TYPE, selectedCol, toggleSelectedCol),
							renderSelectableHeader("information.deadline", GRID_COL.DEADLINE, selectedCol, toggleSelectedCol),
							renderSelectableHeader("information.price", GRID_COL.PRICE, selectedCol, toggleSelectedCol),
						]}
						sizeCell="medium"
					>
						{fields.map((field, index) => (
							<PartBuyerManualAnswer
								key={field.id}
								guidCompanyBuyer={guidCompanyBuyer}
								part={dataParts[index]}
								isLoadingParts={false}
								index={index}
								onGridTab={handleGridTab}
							/>
						))}
					</BoxGrid>
				</StylesShared.GridWrapper>

				<S.AreaButtons>
					<SecondaryButton id="id-buyeranswer-btn-back" type="button" onClick={closeModal}>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<SecondaryButton id="id-buyeranswer-btn-save" type="button" isLoading={isLoadingSave} onClick={onSubmit}>
						{getTranslateByKey("information.save")}
					</SecondaryButton>
				</S.AreaButtons>
				<ModalConfirmBuyerManualAnswer
					openModalConfirm={openModalConfirmBuyerManualAnswer}
					setOpenModalConfirm={setOpenModalConfirmBuyerManualAnswer}
					callbackComfirm={Save}
					control={control}
					setValue={setValue}
					watcherReasonType={watcherReasonType}
				/>

				<KOGModalConfirm
					isOpenModal={openModalConfirmGeneratePurchaseOrder}
					titleModal={getTranslateByKey("information.confirmGeneratePurchaseOrderMessage")}
					setIsOpenModal={setOpenModalConfirmGeneratePurchaseOrder}
					closeModal={() => setOpenModalConfirmGeneratePurchaseOrder(false)}
					onCancel={handleCloseModalConfirmGeneratePurchaseOrder}
					onConfirm={handleConfirmGeneratePurchaseOrder}
				/>

				<ModalConfirmation />
			</S.Container>
		</FormProvider>
	);
}

function WrapperModalBuyerManualAnswer(props: IModalBuyerManualAnswerProps) {
	const [blockedCloseSidebar, setBlockedCloseSidebar] = useState<boolean>(false);

	const [openModalImages, setOpenModalImages] = useState<boolean>(false);
	const isSupplier = userIsSupplier();

	const [openModalConfirmationCloseSidebar, setOpenModalConfirmationCloseSidebar] = useState<boolean>(false);

	function closeSidebar() {
		if (blockedCloseSidebar) {
			setOpenModalConfirmationCloseSidebar(true);
			return;
		}

		props.setIsOpenModal(false);
	}

	function onConfirmModalConfirmation() {
		props.setIsOpenModal(false);
	}

	return (
		<>
			<KogModalSideComponent
				shouldRenderOpen
				title={
					<S.ModalTitleRow>
						{getTranslateByKey("information.newOffer")}
						<SecondaryButton
							id="id-buyeranswer-btn-images"
							type="button"
							onClick={() => setOpenModalImages((isOpen) => !isOpen)}
						>
							{getTranslateByKey("information.images")} / {getTranslateByKey("information.files")}
						</SecondaryButton>
					</S.ModalTitleRow>
				}
				width="97%"
				open={props.isOpenModal}
				isClosedOtherModal={false}
				closeModal={closeSidebar}
			>
				<ModalBuyerManualAnswer {...props} setBlockedCloseSidebar={setBlockedCloseSidebar} />
				{openModalImages && (
					<S.ModalImagesBackdrop id="id-buyeranswer-modalImages-backdrop" onClick={() => setOpenModalImages(false)} />
				)}
			</KogModalSideComponent>

			<ModalImages
				isOpenModal={openModalImages}
				setIsOpenModal={setOpenModalImages}
				closeModal={() => setOpenModalImages(false)}
				canUploadImages={!isSupplier}
				canShowPDF={!isSupplier}
				guidQuote={props.guidQuote}
				guidClaim={props.guidClaim ?? ""}
				canDownloadImages={props.canDownloadImages}
			/>

			<KOGModalConfirm
				isOpenModal={openModalConfirmationCloseSidebar}
				titleModal={getTranslateByKey("information.confirmMessageModalWhenTryCloseSidebarDirectAssign")}
				titleButtonCancel={getTranslateByKey("information.cancel")}
				titleButtonConfirm={getTranslateByKey("information.closeTab")}
				setIsOpenModal={setOpenModalConfirmationCloseSidebar}
				closeModal={() => setOpenModalConfirmationCloseSidebar(false)}
				onCancel={() => setOpenModalConfirmationCloseSidebar(false)}
				onConfirm={onConfirmModalConfirmation}
			/>
		</>
	);
}

export default WrapperModalBuyerManualAnswer;
